class WhatsInBox {
  constructor(container) {
    this.container = container;
    this.productCards = this.container.querySelectorAll('.fsb-whats-in-box__product-card');
    this.errorMessage = this.container.querySelector('.fsb-whats-in-box__error-message');

    // Parse bundle configuration
    this.bundleConfig = JSON.parse(this.container.dataset.bundleConfig || '{}');
    this.bundleVariantId = parseInt(this.container.dataset.bundleVariantId, 10);
    this.colorSwatchOptions = JSON.parse(this.container.dataset.colorSwatchOptions || '[]');
    this.colorConfig = this.parseColorConfig(this.container.dataset.colorConfig || '');
    this.blockSettings = JSON.parse(this.container.dataset.blockSettings || '{}');

    // Track selected variants and options
    this.selectedVariants = new Map();
    this.selectedOptions = new Map();

    // Form Elements
    this.form = document.querySelector('.product-info__buy-buttons .shopify-product-form');
    this.variantIdInput = this.form.querySelector('input[name="id"]')
    this.addToCartButton = this.form.querySelector('button[type="submit"]');
    this.addToCartButtonContent = this.addToCartButton?.innerHTML;

    // Dynamic pricing elements
    this.currencyFormat = this.container.dataset.currencyFormat;
    this.priceSelector = this.blockSettings.price_selector;
    this.compareAtPriceSelector = this.blockSettings.compare_at_price_selector;
    this.priceElements = this.priceSelector ? document.querySelectorAll(this.priceSelector) : [];
    this.compareAtPriceElements = this.compareAtPriceSelector ? document.querySelectorAll(this.compareAtPriceSelector) : [];
    this.isDynamicPricing = this.bundleConfig?.settings?.price?.strategy === 'dynamic_pricing';
    this.discountPercentage = this.isDynamicPricing ? parseFloat(this.bundleConfig?.settings?.price?.value || 0) : 0;

    this.init();
  }

  parseColorConfig(configStr) {
    const colorMap = new Map();
    if (!configStr) return colorMap;

    configStr.split('\n').forEach(line => {
      if (!line.trim()) return;
      const [name, code] = line.split(':').map(s => s.trim());
      if (name && code) {
        colorMap.set(name.toLowerCase(), code);
      }
    });

    return colorMap;
  }

  init() {
    this.setupInitialVariants();
    this.setupColorSwatches();
    this.bindEvents();

    // Initialize pricing if dynamic pricing is enabled
    if (this.isDynamicPricing) {
      this.updateBundlePricing();
    }
  }

  setupColorSwatches() {
    this.productCards.forEach(card => {
      const optionElements = card.querySelectorAll('.fsb-whats-in-box__option');
      optionElements.forEach(optionEl => {
        const optionName = optionEl.dataset.optionName;

        // Check if this option should be a color swatch
        const isColorOption = this.colorSwatchOptions.some(opt =>
          opt.toLowerCase() === optionName.toLowerCase()
        );

        if (isColorOption) {
          const buttons = optionEl.querySelectorAll('.fsb-whats-in-box__color-swatch');
          buttons.forEach(button => {
            const value = button.dataset.value;
            if (value) {
              const colorCode = this.colorConfig.get(value.toLowerCase());
              if (colorCode) {
                button.style.setProperty('--swatch-background', colorCode);
              }
            }
          });
        }
      });
    });
  }

  setupInitialVariants() {
    this.productCards.forEach(card => {
      try {
        const variants = JSON.parse(card.dataset.variants || '[]');
        const appVariants = JSON.parse(card.dataset.appVariants || '{}');
        const categoryTitle = card.dataset.categoryTitle;

        // Update availability for first option before selecting
        const firstOptionEl = card.querySelector('.fsb-whats-in-box__option[data-option-index="0"]');
        if (firstOptionEl) {
          const optionValues = firstOptionEl.querySelectorAll('[data-value]');
          optionValues.forEach(valueEl => {
            const value = valueEl.dataset.value;
            // Check if any variant exists with this first option value
            const hasAvailableVariant = variants.some(v =>
              v.available && v.id in appVariants && v.options[0] === value
            );

            valueEl.disabled = !hasAvailableVariant;
            if (!hasAvailableVariant) {
              valueEl.classList.add('disabled');
            }
          });
        }

        // Find first available variant
        const firstVariant = variants.find(v => v.available && v.id in appVariants);
        if (firstVariant && firstVariant.options) {
          // Select each option value
          firstVariant.options.forEach((optionValue, index) => {
            const option = card.querySelector(`.fsb-whats-in-box__option[data-option-index="${index}"] [data-value="${optionValue}"]`);
            if (option) {
              this.selectOptionValue(option, false);
            }
          });

          // Store the selected variant and options
          this.selectedVariants.set(categoryTitle, firstVariant);
          this.selectedOptions.set(categoryTitle, [...firstVariant.options]);

          // Update product price display for initial variant
          if (this.isDynamicPricing) {
            this.updateProductPrice(card, firstVariant, appVariants);
          }
        }
        this.updateOptionAvailability(card, null)
      } catch (error) {
        console.error('Error setting up initial variants:', error);
      }
    });

    // Check if any product is out of stock and update button text
    this.updateAddToCartButtonState();
  }

  updateOptionAvailability(card, selectedIndex) {
    const variants = JSON.parse(card.dataset.variants || '[]');
    const appVariants = JSON.parse(card.dataset.appVariants || '{}');
    const categoryTitle = card.dataset.categoryTitle;
    const currentOptions = this.selectedOptions.get(categoryTitle) || [];
    const optionElements = card.querySelectorAll('.fsb-whats-in-box__option');

    // For each option in the product
    optionElements.forEach((optionEl, optionIndex) => {
      if (optionIndex !== selectedIndex) { // Skip the currently selected option
        const optionValues = optionEl.querySelectorAll('[data-value]');

        optionValues.forEach(valueEl => {
          const value = valueEl.dataset.value;

          // Create a test array of options with this value
          const testOptions = [...currentOptions];
          testOptions[optionIndex] = value;

          // Check if any variant exists with these options
          const hasAvailableVariant = variants.some(variant => {
            return variant.available && variant.id in appVariants && variant.options.every((opt, i) => {
              return i === optionIndex ? opt === value : opt === currentOptions[i];
            });
          });

          // Disable/enable the option based on availability
          valueEl.disabled = !hasAvailableVariant;
          if (!hasAvailableVariant) {
            valueEl.classList.add('disabled');
            const selectedClass = 'fsb-whats-in-box__option-value--selected';
            valueEl.classList.remove(selectedClass);
          } else {
            valueEl.classList.remove('disabled');
          }
        });
      }
    });
  }

  selectOptionValue(element, updateAvailability = true) {
    const card = element.closest('.fsb-whats-in-box__product-card');
    const optionIndex = parseInt(element.closest('.fsb-whats-in-box__option').dataset.optionIndex, 10);
    const value = element.dataset.value;
    const categoryTitle = card.dataset.categoryTitle;

    // Update selected options
    const currentOptions = this.selectedOptions.get(categoryTitle) || Array(3).fill('');
    currentOptions[optionIndex] = value;
    this.selectedOptions.set(categoryTitle, currentOptions);

    // Update UI
    const isColorSwatch = element.classList.contains('fsb-whats-in-box__color-swatch');
    const selectedClass = isColorSwatch ? 'fsb-whats-in-box__color-swatch--selected' : 'fsb-whats-in-box__option-value--selected';

    // Remove selected class from siblings
    const optionValues = element.closest('.fsb-whats-in-box__option-values');
    optionValues.querySelectorAll('.fsb-whats-in-box__color-swatch--selected, .fsb-whats-in-box__option-value--selected')
      .forEach(el => el.classList.remove(selectedClass));

    // Add selected class to this option
    element.classList.add(selectedClass);

    // Update variant availability
    if (updateAvailability) {
      this.updateOptionAvailability(card, optionIndex);
    }

    // Find and update selected variant
    const variants = JSON.parse(card.dataset.variants || '[]');
    const appVariants = JSON.parse(card.dataset.appVariants || '{}');
    const selectedVariant = variants.find(v =>
      v.available && v.id in appVariants && v.options.every((opt, i) => opt === currentOptions[i])
    );

    if (selectedVariant) {
      this.selectedVariants.set(categoryTitle, selectedVariant);
      // Update product image if variant has an image
      this.updateProductImage(card, selectedVariant);

      // Update product price display
      if (this.isDynamicPricing) {
        this.updateProductPrice(card, selectedVariant, appVariants);
      }
    } else {
      this.selectedVariants.set(categoryTitle, null);
      // Reset to default product image
      this.resetProductImage(card);

      // Clear product price display
      if (this.isDynamicPricing) {
        this.clearProductPrice(card);
      }
    }

    // Update bundle pricing if dynamic pricing is enabled
    if (this.isDynamicPricing) {
      this.updateBundlePricing();
    }

    this.updateLineItemProperties();

    document.dispatchEvent(new CustomEvent('foxsell:mix-and-match-pdp:option-selected', {
      detail: {
        variant: this.selectedVariants
      }
    }));
  }

  updateProductImage(card, variant) {
    const img = card.querySelector('.fsb-whats-in-box__product-image img');
    if (!img) return;

    const variantImages = JSON.parse(card.dataset.variantImages || '[]');
    const defaultImage = card.dataset.productImage;

    // Find the variant's image
    const variantImage = variant.featured_image ? variant.featured_image.src : null;
    const variantImageIndex = variantImages.indexOf(variantImage);

    if (variantImage && variantImageIndex !== -1) {
      img.src = variantImage;
    } else {
      img.src = defaultImage;
    }
  }

  resetProductImage(card) {
    const img = card.querySelector('.fsb-whats-in-box__product-image img');
    if (!img) return;

    const defaultImage = card.dataset.productImage;
    img.src = defaultImage;
  }

  updateLineItemProperties() {
    const selection = Array.from(this.productCards).map(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const categoryQuantity = parseInt(card.dataset.categoryQuantity, 10) || 1;
      const variant = this.selectedVariants.get(categoryTitle);

      if (!variant) return null;

      return {
        category: categoryTitle,
        variantId: variant.id,
        type: "product",
        quantity: categoryQuantity
      };
    });

    // Create unique bundle ID
    const uniqueBundleId = `${this.bundleConfig.bundle_id}_${Date.now()}`;
    
    this.variantIdInput.value = this.bundleVariantId;
    
    const bundleIdInput = this.form.querySelector('.foxsell-bundle-id-input') || document.createElement("input");
    bundleIdInput.type = "hidden";
    bundleIdInput.name = "properties[__foxsell:dynamic_add_on_bundle_id]";
    bundleIdInput.className = "foxsell-bundle-id-input";
    bundleIdInput.value = uniqueBundleId;

    const bundleItemsInput = this.form.querySelector('.foxsell-bundle-items-input') || document.createElement("input");
    bundleItemsInput.type = "hidden";
    bundleItemsInput.name = "properties[__foxsell:dynamic_add_on_bundle_items]";
    bundleItemsInput.className = "foxsell-bundle-items-input";
    bundleItemsInput.value = JSON.stringify(selection);

    this.form.appendChild(bundleIdInput);
    this.form.appendChild(bundleItemsInput);
  }

  addToCart() {
    const selection = Array.from(this.productCards).map(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const categoryQuantity = parseInt(card.dataset.categoryQuantity, 10) || 1;
      const product = JSON.parse(card.dataset.product);
      const variant = this.selectedVariants.get(categoryTitle);

      if (!variant) return null;

      return {
        title: `${product.title}${product.variants.length === 1 ? '' : ' - ' + variant.title}`,
        category: categoryTitle,
        variantId: variant.id,
        type: "product",
        quantity: categoryQuantity
      };
    });

    if (selection.some(item => item === null)) {
      this.showError('Please select options for all products');
      return;
    }

    // Create unique bundle ID
    const uniqueBundleId = `${this.bundleConfig.bundle_id}__${Date.now()}`;

    const displayProperties = selection.reduce((acc, item) => {
      acc[item.title] = (acc[item.title] || 0) + item.quantity;
      return acc;
    }, {});

    // Format data for bundle variant add to cart
    const formData = {
      items: [{
        id: this.bundleVariantId,
        quantity: 1,
        properties: {
          '__foxsell:dynamic_add_on_bundle_id': uniqueBundleId,
          '__foxsell:dynamic_add_on_bundle_items': JSON.stringify(selection),
          ...Object.keys(displayProperties).reduce((acc, key) => {
            acc[key] = `x${displayProperties[key]}`;
            return acc;
          }, {})
        }
      }],
      sections: ['cart-drawer', 'cart-icon-bubble']
    };

    if (this.blockSettings.custom_loading_class) {
      this.addToCartButton.classList.add(this.blockSettings.custom_loading_class);
    }

    // Add to cart using Shopify AJAX API
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
      .then(response => response.json())
      .then(data => {
        if (data.status === 422) {
          throw new Error(data.description || 'Error adding items to cart');
        }

        this.updateCartUI(data);
      })
      .catch(error => {
        console.error('Error:', error);
        this.showError(error.message || 'Error adding items to cart');
      });
  }

  updateCartUI(response) {
    if (additionalSettings?.custom_cart_event) {
      const events = additionalSettings.custom_cart_event.split(',').map(event => event.trim());
      events.forEach(event => {
        document.dispatchEvent(new CustomEvent(event, {
          bubbles: true,
          detail: response
        }));
      });
    }

    if (this.blockSettings.custom_loading_class) {
      this.addToCartButton.classList.remove(this.blockSettings.custom_loading_class);
    }

    const cart = document.querySelector('cart-drawer');
    if (cart) {
      try {
        cart.renderContents(response);
        cart.open();
        if (cart && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      } catch (e) {
        console.error(e);
        if (additionalSettings?.redirect_to_cart !== false) {
          window.location.href = window?.routes?.cart_url || '/cart'
        }
      }
    } else {
      if (additionalSettings?.redirect_to_cart !== false) {
        window.location.href = window?.routes?.cart_url || '/cart'
      }
    }
  }

  updateAddToCartButtonState() {
    if (!this.addToCartButton) return;

    // Check if any selected variant is null (out of stock or not selected)
    const anyOutOfStock = Array.from(this.productCards).some(card => {
      const variants = JSON.parse(card.dataset.variants || '[]');
      const appVariants = JSON.parse(card.dataset.appVariants || '{}');

      // Check if there's any available variant matching current options
      const hasAvailableVariant = variants.some(v =>
        v.available && v.id in appVariants
      );

      return !hasAvailableVariant;
    });

    if (anyOutOfStock) {
      this.addToCartButton.textContent = this.blockSettings.sold_out_text || 'Sold Out';
      this.addToCartButton.disabled = true;
    } else {
      if (this.addToCartButtonContent) {
        this.addToCartButton.innerHTML = this.addToCartButtonContent;
      } else {
        this.addToCartButton.textContent = this.blockSettings.add_to_cart_text || 'Add to Cart';
      }
      this.addToCartButton.disabled = false;
    }
  }

  bindEvents() {
    // Handle option selection
    this.container.addEventListener('click', (e) => {
      const optionValue = e.target.closest('[data-value]');
      if (optionValue && !optionValue.disabled) {
        this.selectOptionValue(optionValue);
      }
    });

    this.form.addEventListener('submit', () => {
      this.updateLineItemProperties();
    });

    // Handle add to cart
    // if (this.addToCartButton) {
    // this.addToCartButton.addEventListener('click', () => this.addToCart());
    // }
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.style.display = 'block';
      setTimeout(() => {
        this.errorMessage.style.display = 'none';
      }, 3000);
    }
  }

  /**
   * Format price according to the current currency
   * @param {number} price - Price in cents
   * @returns {string} - Formatted price
   */
  formatPrice(cents) {
    if (typeof cents == 'string') { cents = cents.replace('.', ''); }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = this.currencyFormat;

    function defaultOption(opt, def) {
      return (typeof opt == 'undefined' ? def : opt);
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) { return 0; }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.'),
        dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
        cents = parts[1] ? (decimal + parts[1]) : '';

      return dollars + cents;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  };

  /**
   * Update the price display for a specific product
   * @param {HTMLElement} card - Product card element
   * @param {Object} variant - Selected variant
   * @param {Object} appVariants - App variants data
   */
  updateProductPrice(card, variant, appVariants) {
    if (!variant || !appVariants || !(variant.id in appVariants)) return;

    const priceContainer = card.querySelector('.fsb-whats-in-box__product-price');
    if (!priceContainer) return;

    const price = appVariants[variant.id];
    if (price) {
      priceContainer.textContent = this.formatPrice(parseFloat(price) * 100);
    } else {
      priceContainer.textContent = '';
    }
  }

  /**
   * Clear the price display for a product
   * @param {HTMLElement} card - Product card element
   */
  clearProductPrice(card) {
    const priceContainer = card.querySelector('.fsb-whats-in-box__product-price');
    if (priceContainer) {
      priceContainer.textContent = '';
    }
  }

  /**
   * Update the bundle pricing based on selected variants
   */
  updateBundlePricing() {
    // Check if any product is out of stock or not selected
    const anyOutOfStock = Array.from(this.productCards).some(card => {
      const categoryTitle = card.dataset.categoryTitle;
      return !this.selectedVariants.get(categoryTitle);
    });

    // Hide prices if any product is out of stock or not selected
    if (anyOutOfStock) {
      this.hideBundlePrices();
      return;
    }

    // Calculate total price of all selected variants
    let totalPrice = 0;
    this.productCards.forEach(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const categoryQuantity = parseInt(card.dataset.categoryQuantity, 10) || 1;
      const variant = this.selectedVariants.get(categoryTitle);
      const appVariants = JSON.parse(card.dataset.appVariants || '{}');

      if (variant && variant.id in appVariants) {
        const variantPrice = appVariants[variant.id];
        totalPrice += variantPrice * categoryQuantity;
      }
    });

    // Apply discount if applicable
    if (totalPrice > 0) {
      const discountedPrice = this.discountPercentage > 0 ?
        totalPrice * (1 - this.discountPercentage / 100) : totalPrice;

      // Update price elements
      this.updateBundlePriceDisplay(totalPrice, discountedPrice);
    } else {
      this.hideBundlePrices();
    }
  }

  /**
   * Update the bundle price display with formatted prices
   * @param {number} originalPrice - Original price in cents
   * @param {number} discountedPrice - Discounted price in cents
   */
  updateBundlePriceDisplay(originalPrice, discountedPrice) {
    // Format prices
    const formattedOriginalPrice = this.formatPrice(originalPrice * 100);
    const formattedDiscountedPrice = this.formatPrice(discountedPrice * 100);

    // Update compare-at price elements if discount is applied
    if (this.discountPercentage > 0 && this.compareAtPriceElements.length > 0) {
      this.compareAtPriceElements.forEach(element => {
        element.textContent = formattedOriginalPrice;
        element.style.display = 'inline';
      });
    } else if (this.compareAtPriceElements.length > 0) {
      this.compareAtPriceElements.forEach(element => {
        element.style.display = 'none';
      });
    }

    // Update price elements
    if (this.priceElements.length > 0) {
      this.priceElements.forEach(element => {
        element.textContent = formattedDiscountedPrice;
        element.style.display = 'inline';
      });
    }
  }

  /**
   * Hide all bundle price elements
   */
  hideBundlePrices() {
    if (this.priceElements.length > 0) {
      this.priceElements.forEach(element => {
        element.style.display = 'none';
      });
    }

    if (this.compareAtPriceElements.length > 0) {
      this.compareAtPriceElements.forEach(element => {
        element.style.display = 'none';
      });
    }
  }
}

// Initialize the component
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.fsb-whats-in-box');
  containers.forEach(container => container._whatsInBox = new WhatsInBox(container));
});
